import {inject} from 'vue';
import type {BuildQueryURLArgs, Query} from '@prismicio/client';
import type {PrismicDocument} from '@prismicio/types';
import {callWithNuxt, useNuxtApp} from '#app';
import {useLocale, useLogger, usePrismicLang, usePrismicRedirect, useRoute, useSpanWrap} from '#imports';
import {PRISMIC_CLIENT} from '~/keys';

/**
 * Retrieve document from Prismic repository.
 * @param documentType  Document type.
 * @param uid           Document UID.
 */
export function usePrismicDocument(documentType: string, uid?: string) {
    const nuxtApp = useNuxtApp();
    const logger = useLogger();
    const client = inject(PRISMIC_CLIENT);
    const route = useRoute();
    const {locale} = useLocale();

    const {lang} = usePrismicLang();

    const {fetchRedirect} = usePrismicRedirect(route.path, locale.value);

    const fetchDocument = async (params?: {fetchLinks?: string[]}): Promise<PrismicDocument> => {
        try {
            const getPrismicDocument = () =>
                uid
                    ? client!.getByUID(documentType, uid, {lang: lang.value, ...params})
                    : client!.getSingle(documentType, {lang: lang.value, ...params});
            return await useSpanWrap(getPrismicDocument, {httpMethod: 'prismic.get', kind: 'prismic', name: `[prismic] ${documentType}`});
        } catch (err: any) {
            logger.info(
                `Could not retrieve document from Prismic with parameters: [${documentType}, ${locale.value}, ${uid}]. Error message=${err.message}. Trying redirection...`,
            );
        }
        // If the document does not exist, check for redirect or throw 404
        return await callWithNuxt(nuxtApp, fetchRedirect);
    };

    async function fetchDocuments(params?: Partial<BuildQueryURLArgs>): Promise<Query | undefined> {
        try {
            return await useSpanWrap(() => client!.getByType(documentType, {lang: lang.value, ...params}), {
                httpMethod: 'prismic.get',
                kind: 'prismic',
                name: `[prismic] ${documentType}`,
            });
        } catch (err: any) {
            logger.info(
                `Could not retrieve documents from Prismic with parameters: [${documentType}, ${locale.value}]. Error message=${err.message}.`,
            );
        }

        return undefined;
    }

    return {
        fetchDocument,
        fetchDocuments,
    };
}
